import { Box, Chip, Divider, Typography } from '@mui/material'
import React, { useState } from 'react'
import { orderPanelStyles } from './common/orderPanelStyles'
import OrderUserAvatar from './OrderUserAvatar'
import { mainBlue, mainGreen, mainRed } from '../utils/color'
import { transformDetailWithPhone } from '../utils/transformDetail'
import ListPinItem from './ListPinItem'
import { useSelector } from 'react-redux'
import ImageViewerModal from './ImageViewerModal'

export default function PanelInternalOrderItem({ order, index, renderActions }) {
	const currentStore = useSelector((s) => s.currentUser?.currentUser?.store)
	const orderTitle = getTitle(order, currentStore)
	const avatarUrl = getAvatar(order, currentStore)
	const orderData = order?.order
	const [selectedImage, setSelectedImage] = useState(null)

	return (
		<Box key={order?._id || index} sx={orderPanelStyles.orderCard}>
			{/* Header with Avatar and Title */}
			<Box sx={orderPanelStyles.header}>
				<OrderUserAvatar url={avatarUrl} />
				<Typography sx={orderPanelStyles.title}>{orderTitle}</Typography>
			</Box>

			<Box sx={{ display: 'flex', mt: 1 }}>
				<StatusChip order={order} currentStore={currentStore} />
			</Box>

			<Box sx={{ mt: 2 }}>
				{/* Order Total */}
				<Typography sx={styles.total}>
					{orderData?.freshQuantity || 0}Kg sống / {orderData?.cookedQuantity || 0}Kg chín /{' '}
					{(orderData?.totalPrice || 0).toLocaleString()}đ
				</Typography>

				{/* Order Items Grid */}
				<ListPinItem line_items={orderData?.line_items} />

				<Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
					{orderData.detail && (
						<>
							<Divider sx={{ bgcolor: 'primary.main', opacity: 0.5 }} />
							<Box sx={{ textAlign: 'end' }}>
								<Typography
									variant='body1'
									dangerouslySetInnerHTML={{ __html: transformDetailWithPhone(orderData.detail) }}
								/>
							</Box>
						</>
					)}

					<Divider sx={{ bgcolor: 'primary.main', opacity: 0.5 }} />

					{orderData.note && (
						<Box sx={{ textAlign: 'end' }}>
							<Typography variant='body1'>{orderData.note}</Typography>
						</Box>
					)}

					<Box sx={{ textAlign: 'end' }}>
						<Typography variant='body1'>
							Thanh toán:{' '}
							{orderData.payment === 'transfer' ? 'Chuyển khoản' : orderData.payment === 'cash' ? 'Tiền mặt' : 'Nhanh'}
						</Typography>
					</Box>

					{orderData.shippingTotal != null && (
						<Box sx={{ textAlign: 'end' }}>
							<Typography variant='body1'>
								Phí ship: {new Intl.NumberFormat().format(orderData.shippingTotal)}đ
							</Typography>
						</Box>
					)}

					{/* show photoAttaches */}
					<Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 1 }}>
						{orderData?.photoAttaches?.map((photo, index) => (
							<Box key={index} sx={{ position: 'relative' }}>
								<img
									loading='lazy'
									style={styles.previewImage}
									src={photo}
									alt={`existing-${index}`}
									onClick={() => setSelectedImage(photo)}
								/>
							</Box>
						))}
					</Box>
				</Box>
			</Box>
			<ImageViewerModal open={!!selectedImage} onClose={() => setSelectedImage(null)} src={selectedImage} />
			{renderActions && renderActions(order)}
		</Box>
	)
}

function getTitle(order, currentStore) {
	if (order?.fromStore?._id === currentStore) {
		return (
			<span>
				<b style={{ color: mainBlue }}>{order?.sender?.name}</b> đã tạo đơn{' '}
				<b style={{ color: order.orderType === 'SELL' ? mainRed : mainGreen }}>
					{order.orderType === 'SELL' ? 'BÁN' : 'MUA'}
				</b>{' '}
				tới cửa hàng <b style={{ color: mainBlue }}>{order?.toStore?.name}</b> vào {order?.timeStamp}
			</span>
		)
	} else {
		return (
			<span>
				{order?.status === 'accept' ? (
					<span>
						<b style={{ color: mainBlue }}>{order?.checker?.name}</b> đã duyệt
					</span>
				) : order?.status === 'reject' ? (
					<span>
						<b style={{ color: mainBlue }}>{order?.checker?.name}</b> đã từ chối
					</span>
				) : (
					<span>Chờ duyệt</span>
				)}{' '}
				đơn:{' '}
				<b style={{ color: order.orderType === 'SELL' ? mainGreen : mainRed }}>
					{order.orderType === 'SELL' ? 'MUA' : 'BÁN'}
				</b>{' '}
				{order.orderType === 'SELL' ? 'từ' : 'cho'} cửa hàng <b style={{ color: mainBlue }}>{order?.fromStore?.name}</b>{' '}
				vào {order?.timeStamp}
			</span>
		)
	}
}

function StatusChip({ order, currentStore }) {
	return (
		<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
			<Chip
				label={order?.status === 'pending' ? 'ĐANG XỬ LÝ' : order?.status === 'accept' ? 'CHẤP NHẬN' : 'TỪ CHỐI'}
				sx={
					order?.status === 'pending'
						? chipStyles.greyChip
						: order?.status === 'accept'
							? chipStyles.greenChip
							: chipStyles.redChip
				}
			/>
			{order?.fromStore?._id === currentStore ? (
				<Chip
					label={order?.orderType === 'SELL' ? 'BÁN' : 'MUA'}
					sx={order?.orderType === 'SELL' ? chipStyles.redChip : chipStyles.greenChip}
				/>
			) : (
				<Chip
					label={order?.orderType === 'SELL' ? 'MUA' : 'BÁN'}
					sx={order?.orderType === 'SELL' ? chipStyles.greenChip : chipStyles.redChip}
				/>
			)}
			{order?.editor ? <Chip label={`Đã sửa`} sx={chipStyles.greyChip} /> : null}
		</Box>
	)
}

function getAvatar(order, currentStore) {
	if (order?.fromStore?._id === currentStore) {
		return order?.sender?.photo ?? ''
	}
	if (order?.toStore?._id === currentStore) {
		return order?.checker?.photo ?? ''
	}
	return order?.sender?.photo ?? ''
}

const chipStyles = {
	greenChip: {
		backgroundColor: mainGreen,
		color: 'white',
		borderRadius: '16px',
		fontWeight: 500,
		textAlign: 'center',
		height: '24px',
	},
	redChip: {
		backgroundColor: mainRed,
		color: 'white',
		fontWeight: 500,
		borderRadius: '16px',
		fontSize: '12px',
		height: '24px',
	},
	greyChip: {
		backgroundColor: '#e0e0e0',
		color: 'grey',
		fontWeight: 500,
		borderRadius: '16px',
		fontSize: '12px',
		height: '24px',
	},
}

const styles = {
	total: {
		color: '#4CAF50',
		fontSize: '24px',
		fontWeight: 500,
		textAlign: 'center',
		mb: 2,
	},
	previewImage: {
		width: '60px',
		height: '60px',
		objectFit: 'cover',
		borderRadius: '8px',
		cursor: 'pointer',
		marginRight: '8px',
		marginBottom: '8px',
	},
}

